import {
	resolveEstimateEnd,
	resolveTicketSpan,
	resolveWorkStart,
	isWorkedDay,
} from "./ticket-span";

/** What one day of a coverage bar says about the work standing on it. */
export type CoverageDay = "staffed" | "unstaffed" | null;

/** What a coverage bar needs to know about one ticket: where its work sits, and who is on it. */
export interface CoverageTicket {
	startDate: string;
	endDate: string;
	dateMode: "date" | "week" | null;
	/** The weekdays the ticket's site works. Absent falls back to Monday to Friday. */
	weekdays?: number[] | null;
	/** The days a break stands on. */
	excludedDates?: string[] | null;
	workStartDate: string | null;
	estimatedDays: number;
	/** Whether any crew is scheduled onto this ticket at all. */
	isStaffed: boolean;
}

/**
 * Roll a set of tickets up into one day-by-day reading of a row above them.
 *
 * This is what a project or a manager looks like with its tickets closed: which days carry work,
 * and whether anybody is on it. **Unstaffed wins a day that carries both**, because the reason to
 * scan a collapsed row is to find the days nobody is coming — a day reported as covered because
 * three of its four tickets are staffed hides exactly the thing being looked for.
 *
 * Each ticket is measured the way its own row measures it, so a collapsed project and the rows it
 * opens into cannot disagree about which days it occupies: the same span resolution, the same
 * weekdays, the same breaks.
 *
 * @param dates - The board's window, one stored `YYYY-MM-DD` per column, in order.
 * @param tickets - The tickets standing under the row.
 * @returns One entry per column, null where no ticket has work that day.
 */
export function buildTicketCoverage(dates: string[], tickets: CoverageTicket[]): CoverageDay[] {
	const coverage: CoverageDay[] = dates.map(() => null);

	for (const ticket of tickets) {
		const span = resolveTicketSpan(ticket);
		const workStart = resolveWorkStart(span, ticket.estimatedDays, ticket.workStartDate);
		const workEnd = resolveEstimateEnd(span, ticket.estimatedDays, workStart) ?? span.end;

		for (let index = 0; index < dates.length; index++) {
			const date = dates[index];
			if (!date || date < workStart || date > workEnd) continue;
			if (!isWorkedDay(span, date)) continue;
			// A day already known to be short of a crew stays that way whatever else stands on it.
			if (coverage[index] === "unstaffed") continue;
			coverage[index] = ticket.isStaffed ? "staffed" : "unstaffed";
		}
	}

	return coverage;
}
